<template>
  <div class="card-key qm-page-bg">
    <div class="hero-section">
      <div class="brand-mark">
        <div class="brand-icon">自</div>
      </div>
      <h1 class="hero-title">自助下单</h1>
      <p class="hero-subtitle">输入卡密，即刻开启您的专属点单体验</p>
    </div>

    <div class="card-input-container qm-glass-card">
      <div class="input-label">卡密验证</div>
      <van-cell-group :border="false">
        <van-field
          v-model="cardKey"
          placeholder="请输入您的卡密"
          :error="!!error"
          :error-message="error"
          @input="clearError"
          class="key-field"
        />
      </van-cell-group>
      <div class="tip-message">
        <van-icon name="info-o" />
        <span>链接即买即用，不支持囤货</span>
      </div>
      <div class="button-container" :class="{ 'has-order': recentOrders.length > 0 }">
        <van-button
          type="primary"
          :block="recentOrders.length === 0"
          class="qm-btn-primary submit-btn"
          @click="submitCardKey"
          :loading="loading"
        >
          立即验证
        </van-button>
        <van-button
          v-if="recentOrders.length > 0"
          type="info"
          plain
          icon="orders-o"
          class="order-btn"
          @click="showOrderList = true"
        >
          查订单 ({{ recentOrders.length }})
        </van-button>
      </div>
    </div>

    <van-popup v-model="showOrderList" position="bottom" round closeable :style="{ height: '70%' }">
      <div class="order-list-popup">
        <div class="popup-title">
          <span class="title-dot"></span>
          选择要查看的订单
        </div>
        <div class="order-list">
          <div
            v-for="order in recentOrders"
            :key="order.orderBaseInfo.orderNo"
            class="order-item qm-glass-card"
            @click="goToOrderDetail(order.orderBaseInfo.orderNo)"
          >
            <div class="order-info">
              <div class="shop-name">
                <van-icon name="shop-o" />
                {{ order.shopInfo.name }}
              </div>
              <div class="goods-info">
                <span class="goods-name">{{ getGoodsNames(order) }}</span>
                <span class="goods-count">共{{ order.orderBaseInfo.goodsTotalNum }}件</span>
              </div>
              <div class="order-meta">
                <span class="order-time">{{ order.orderBaseInfo.orderAt }}</span>
                <van-tag :type="order.orderBaseInfo.stateText === '已完成' ? 'success' : 'primary'">
                  {{ order.orderBaseInfo.stateText }}
                </van-tag>
              </div>
            </div>
            <van-icon name="arrow" class="arrow-icon" />
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import request from "@/utils/request";
import { parseOriginalData } from "@/utils/tokenRefresh";

export default {
  name: "CardKey",
  data() {
    return {
      cardKey: "",
      error: "",
      loading: false,
      showOrderList: false,
      recentOrders: [],
    };
  },
  computed: {
    ...mapState(["qmUserToken", "appConfig"]),
  },
  created() {
    // 先清空缓存的qmUserToken和localStorage
    this.$store.dispatch("clearQmUserToken");
    this.$store.dispatch("clearMemberCoupon");
    this.$store.dispatch("clearUuid");
    this.$store.dispatch("clearWxid");
    this.$store.dispatch("clearRefreshChannel");
    this.$store.dispatch("clearUseWallet");

    // 如果已经有卡密，直接跳转到首页
    if (this.qmUserToken) {
      this.$router.push("/shop-list");
      return;
    }

    // 检查URL参数中是否有加密数据
    // 优先从 Vue Router 获取，如果获取不到则从原始 URL 获取（兼容 hash 模式下参数在 # 之前的情况）
    let encryptedData = this.$route.query.uuid;
    if (!encryptedData) {
      const urlParams = new URLSearchParams(window.location.search);
      encryptedData = urlParams.get('uuid');
    }
    if (encryptedData) {
      this.cardKey = encryptedData;
      this.submitCardKey();
    }
  },
  methods: {
    ...mapActions(["setQmUserToken", "setMemberCoupon", "setUuid", "setWxid", "setRefreshChannel", "setTfCardKey", "setUseWallet"]),
    clearError() {
      this.error = "";
    },
    goToOrderDetail(orderNo) {
      this.showOrderList = false;
      this.$router.push({
        path: "/order-detail",
        query: { orderNo },
      });
    },
    getGoodsNames(order) {
      if (!order.itemsDetail || order.itemsDetail.length === 0) {
        return '无商品信息';
      }
      const allItems = order.itemsDetail.flatMap(detail => detail.items || []);
      if (allItems.length === 0) {
        return '无商品信息';
      }
      // 获取第一个商品名称
      const firstName = allItems[0].itemName;
      // 如果只有一个商品，直接返回
      if (allItems.length === 1) {
        return firstName;
      }
      // 如果有多个商品，返回"商品名等N件"
      return `${firstName}等${allItems.length}件`;
    },
    async submitCardKey() {
      if (!this.cardKey.trim()) {
        this.error = "请输入卡密";
        return;
      }
      this.loading = true;

      try {
        // 调用后端解密接口
        const response = await request.post("/encrypt/get-data", {
          uuid: this.cardKey.trim(),
        });

        if (!response) {
          throw new Error(response.message || "卡密验证失败");
        }

        const decryptedText = response.data.originalData;
        const {
          wxid,
          qmUserToken,
          memberCouponId,
          teaType,
          isMemberCodeLink,
          refreshChannel,
          tfCardKey,
        } = parseOriginalData(decryptedText);

        await this.setQmUserToken(qmUserToken);
        await this.setUuid(this.cardKey.trim());
        await this.setWxid(wxid);
        await this.setRefreshChannel(refreshChannel);
        await this.setTfCardKey(tfCardKey);
        await this.setUseWallet(response.data.useWallet === 1);
        
        // 从接口获取配置列表
        const configResponse = await request.get("/appConfig/list");
        if (!configResponse || !configResponse.success) {
          throw new Error("获取配置列表失败");
        }
        
        // 过滤出 is_qm === 1 的配置，并根据 type_key 匹配
        const configList = configResponse.data.filter(item => item.is_qm === 1);
        const matchedConfig = configList.find(item => item.type_key == teaType);
        
        if (!matchedConfig) {
          this.$toast.fail("未找到对应的配置，请联系客服");
          this.loading = false;
          return;
        }
        
        const defaultConfig = {
          storeId: matchedConfig.storeid,
          appid: matchedConfig.appid,
          name: matchedConfig.name,
          dataId: memberCouponId
        };
        this.$store.commit("SET_APP_CONFIG", defaultConfig);

        // 记录卡密是否已被使用
        const isCardUsed = response.data.isUsed === 1;

        // 调用订单列表接口
        try {
          const orderListResponse = await request.post("/qimai/order/list", {
            pageNo: 1,
            pageSize: 10,
            orderAtStart: "2024-07-21 00:00:00",
            orderAtEnd:
              new Date().getFullYear() +
              "-" +
              String(new Date().getMonth() + 1).padStart(2, "0") +
              "-" +
              String(new Date().getDate()).padStart(2, "0") +
              " 23:59:59",
            type: 0,
            state: "ALL",
            appid: this.appConfig.appid,
            storeId: this.appConfig.storeId,
            qmUserToken: qmUserToken,
          });


          // console.log(orderListResponse);
          // if(orderListResponse){
          //   orderListResponse = orderListResponse.data
          // }

          // 检查是否有符合条件的订单
          if (
            orderListResponse.data &&
            orderListResponse.data.data.length > 0
          ) {
            // const now = new Date();
            // const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000); // 一小时前的时间

            // 查找所有未取消且使用了相同优惠券的订单
            const matchedOrders = orderListResponse.data.data.filter((order) => {
              // const orderTime = new Date(order.orderBaseInfo.orderAt);
              // const isWithinOneHour = orderTime > oneHourAgo;
              const isNotCancelled = !["已取消", "待支付"].includes(
                order.orderBaseInfo.stateText
              );
              const isSameCouponId =
                order.discountLists.length>0 &&
                memberCouponId == order.discountLists[0].extraMap["templeteId"];
              // return isWithinOneHour && isNotCancelled && isSameCouponId;
              return isNotCancelled && isSameCouponId;
            });

            if (matchedOrders.length > 0 && isCardUsed) {
              // 只有在卡密已被使用的情况下，显示订单查询按钮
              this.recentOrders = matchedOrders.slice(0, 10);
              this.loading = false;
              
              // 简短提示，不阻塞
              this.$toast({
                message: '该链接已使用，可查看订单',
                duration: 2000
              });
              
              return; // 停止后续流程，用户可通过按钮查看订单
            }
            // 如果卡密未使用，即使有订单，也继续正常流程（不显示订单列表）
          }
        } catch (orderError) {
          console.error("获取订单列表失败：", orderError);
        }

        // 如果卡密已被使用，且没有找到订单（上面的 return 没有执行），则提示用户
        if (isCardUsed) {
          this.$toast.fail("该卡密已使用，请联系客服更换");
          this.loading = false;
          return;
        }

        let list = await request.post("/qimai/coupon/list", {
          qmUserToken: this.qmUserToken,
        });

        console.log("list",list);
        
        let memberCouponObj = list.data.data.find(
          (item) => item.couponTemplate.id === memberCouponId
        );

        console.log(memberCouponObj);
        

        if (!memberCouponObj) {
          this.$toast("该账号无对应免单卷，请联系客服售后处理");
          this.loading = false;
          return;
        }
        await this.setMemberCoupon(memberCouponObj);

        this.$router.push(isMemberCodeLink ? "/member-code" : "/shop-list");
      } catch (error) {
        console.error("卡密验证失败:", error);
        this.error = error.message || "卡密验证失败";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-key {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
}

.hero-section {
  text-align: center;
  margin-bottom: 40px;

  .brand-mark {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    .brand-icon {
      width: 72px;
      height: 72px;
      border-radius: 22px;
      background: linear-gradient(135deg, var(--qm-accent), var(--qm-secondary));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      font-weight: 800;
      color: #1a1200;
      box-shadow: var(--qm-shadow-glow);
    }
  }

  .hero-title {
    font-size: 28px;
    font-weight: 700;
    color: var(--qm-text-primary);
    letter-spacing: 2px;
    margin-bottom: 10px;
  }

  .hero-subtitle {
    font-size: 14px;
    color: var(--qm-text-secondary);
    letter-spacing: 0.5px;
  }
}

.card-input-container {
  width: 100%;
  max-width: 400px;
  padding: 28px 24px;
  box-shadow: var(--qm-shadow);

  .input-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--qm-accent);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  .key-field {
    background: var(--qm-bg-input);
    border-radius: var(--qm-radius-md);
    border: 1px solid var(--qm-border);
    margin-bottom: 4px;

    :deep(.van-field__control) {
      font-size: 16px;
      letter-spacing: 1px;
    }
  }
}

.button-container {
  margin-top: 24px;

  .submit-btn {
    height: 50px;
    font-size: 16px;
  }

  &.has-order {
    display: flex;
    gap: 12px;

    .van-button {
      flex: 1;
      height: 50px;
    }
  }
}

.tip-message {
  margin-top: 16px;
  padding: 10px 14px;
  background: rgba(245, 166, 35, 0.08);
  border: 1px solid rgba(245, 166, 35, 0.2);
  border-radius: var(--qm-radius-sm);
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--qm-text-secondary);

  .van-icon {
    color: var(--qm-accent);
    margin-right: 6px;
    font-size: 14px;
  }
}

.order-list-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--qm-bg-surface);

  .popup-title {
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    padding: 20px 16px;
    color: var(--qm-text-primary);
    border-bottom: 1px solid var(--qm-border);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .title-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--qm-accent);
    }
  }

  .order-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: var(--qm-transition);

    &:active {
      background: var(--qm-bg-card-hover);
    }

    .order-info {
      flex: 1;

      .shop-name {
        font-size: 15px;
        font-weight: 600;
        color: var(--qm-text-primary);
        margin-bottom: 10px;
        display: flex;
        align-items: center;

        .van-icon {
          color: var(--qm-accent);
          margin-right: 8px;
          font-size: 16px;
        }
      }

      .goods-info {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .goods-name {
          font-size: 13px;
          color: var(--qm-text-secondary);
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .goods-count {
          font-size: 12px;
          color: var(--qm-text-muted);
          margin-left: 8px;
        }
      }

      .order-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .order-time {
          font-size: 12px;
          color: var(--qm-text-muted);
        }
      }
    }

    .arrow-icon {
      color: var(--qm-text-muted);
      font-size: 16px;
      margin-left: 8px;
    }
  }
}
</style> 